/**
 * ---------------------------------------------------------------------------------------------------------------------
 * PERSONALIZATION
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Drives the personalization module (see snippets/personalization.liquid).
 *
 * When at least one add-on is selected, this element takes over the product form submission so the base product and its
 * add-ons can be sent as a single multi-item /cart/add.js request, all sharing a "_linked_id" property. When nothing is
 * selected it stays out of the way entirely and the theme's own <product-form> handles the submit as usual.
 */

(() => {
  if (window.customElements.get('personalization-form')) {151065100519
    return;
  }

  /**
   * Minimal implementation of Shopify's money formatter, driven by shop.money_format (exposed through js-variables).
   */
  const formatMoney = (cents) => {
    const format =
      window.themeVariables?.settings?.[
        window.themeVariables?.settings?.currencyCodeEnabled ? 'moneyWithCurrencyFormat' : 'moneyFormat'
      ] || '${{amount}}';

    const withDelimiters = (amount, precision, thousands, decimal) => {
      const value = (amount / 100.0).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);

      return parts[1] ? `${dollars}${decimal}${parts[1]}` : dollars;
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          return withDelimiters(cents, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(cents, 0, ' ', ',');
        case 'amount_with_space_separator':
          return withDelimiters(cents, 2, ' ', ',');
        default:
          return withDelimiters(cents, 2, ',', '.');
      }
    });
  };

  const generateLinkedId = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  };

  class PersonalizationForm extends HTMLElement {
    #abortController;

    connectedCallback() {
      this.#abortController = new AbortController();

      const { signal } = this.#abortController;

      this.addEventListener('change', this.#onChange.bind(this), { signal });
      this.addEventListener('input', this.#onInput.bind(this), { signal });

      // IMPLEMENTATION NOTE: the listener is bound to the document rather than to the form itself. At the target phase
      // listeners run in registration order and the capture flag is ignored, so binding to the form would give us no
      // guarantee of running before the theme's <product-form> listener. Capturing on an ancestor always runs first,
      // and stopping propagation there means the event never reaches the form at all.
      document.documentElement.addEventListener('submit', this.#onSubmit.bind(this), { capture: true, signal });

      this.items.forEach((item) => this.#syncItemState(item));
      this.#updateTotal();
      this.#updatePaymentButton();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    get form() {
      return document.forms[this.getAttribute('form')];
    }

    get items() {
      return Array.from(this.querySelectorAll('[data-addon]'));
    }

    get selectedItems() {
      return this.items.filter((item) => item.querySelector('[data-personalization-toggle]').checked);
    }

    get maxLength() {
      return parseInt(this.dataset.engravingMaxLength, 10) || 0;
    }

    get pattern() {
      return this.dataset.engravingPattern ? new RegExp(this.dataset.engravingPattern) : null;
    }

    #onChange(event) {
      const item = event.target.closest('[data-addon]');

      if (!item) {
        return;
      }

      if (event.target.matches('[data-personalization-toggle]')) {
        this.#syncItemState(item);
      }

      if (event.target.matches('select')) {
        this.#updatePrice(item);
      }

      this.#setError(item, '');
      this.#updateTotal();
      this.#updatePaymentButton();
    }

    #onInput(event) {
      if (!event.target.matches('[data-personalization-text]')) {
        return;
      }

      const item = event.target.closest('[data-addon]');
      const { pattern } = this;

      if (pattern && !pattern.test(event.target.value)) {
        // Strip whatever the pattern rejects rather than silently accepting it (paste, IME, mobile keyboards)
        event.target.value = Array.from(event.target.value)
          .filter((character) => pattern.test(character))
          .join('');

        this.#setError(item, this.dataset.errorInvalidCharacters);
      } else {
        this.#setError(item, '');
      }

      this.#updateCounter(item);
    }

    #syncItemState(item) {
      const enabled = item.querySelector('[data-personalization-toggle]').checked;

      item.querySelectorAll('select, [data-personalization-text]').forEach((control) => {
        control.disabled = !enabled;
      });

      if (!enabled) {
        this.#setError(item, '');
      }
    }

    #updatePrice(item) {
      const select = item.querySelector('select');
      const priceElement = item.querySelector('[data-personalization-price]');
      const option = select?.selectedOptions?.[0];

      if (!option?.dataset.price) {
        return;
      }

      priceElement.textContent = option.dataset.price;
      item.dataset.priceCents = option.dataset.priceCents;
    }

    #updateCounter(item) {
      const input = item.querySelector('[data-personalization-text]');
      const counter = item.querySelector('[data-personalization-counter]');

      if (input && counter) {
        counter.textContent = `${input.value.length}/${this.maxLength}`;
      }
    }

    #updateTotal() {
      const total = this.querySelector('[data-personalization-total]');

      if (!total) {
        return;
      }

      const cents = this.selectedItems.reduce((sum, item) => sum + (parseInt(item.dataset.priceCents, 10) || 0), 0);

      total.hidden = cents === 0;
      this.querySelector('[data-personalization-total-value]').textContent = formatMoney(cents);
    }

    /**
     * The dynamic checkout button checks out straight from the form without ever firing a submit event, which would
     * silently drop the add-ons. It is hidden for as long as anything is personalized.
     */
    #updatePaymentButton() {
      const paymentButton = this.form?.querySelector('.shopify-payment-button');

      if (paymentButton) {
        paymentButton.hidden = this.selectedItems.length > 0;
      }
    }

    #setError(item, message) {
      const errorElement = item.querySelector('[data-personalization-error]');

      if (errorElement) {
        errorElement.textContent = message || '';
      }
    }

    /**
     * Returns the variant ID for an add-on, or null when the customer still has to make a choice.
     */
    #variantIdFor(item) {
      const select = item.querySelector('select');

      if (select) {
        return select.value || null;
      }

      return item.querySelector('[data-personalization-variant]')?.value || null;
    }

    #validate() {
      let valid = true;

      this.selectedItems.forEach((item) => {
        if (!this.#variantIdFor(item)) {
          this.#setError(item, this.dataset.errorRequired);
          valid = false;
          return;
        }

        const textInput = item.querySelector('[data-personalization-text]');

        if (!textInput) {
          return;
        }

        if (textInput.value.trim() === '') {
          this.#setError(item, this.dataset.errorRequired);
          valid = false;
        } else if (this.pattern && !this.pattern.test(textInput.value)) {
          this.#setError(item, this.dataset.errorInvalidCharacters);
          valid = false;
        }
      });

      if (!valid) {
        this.querySelector('.personalization__error:not(:empty)')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }

      return valid;
    }

    /**
     * Builds the /cart/add.js payload. The base item inherits everything the theme form already carries (quantity,
     * selling plan, and any line item property blocks), and every item receives the shared linking properties.
     */
    #buildItems(linkedId) {
      const formData = new FormData(this.form);
      const quantity = parseInt(formData.get('quantity'), 10) || 1;

      const baseProperties = { _linked_id: linkedId, _linked_role: 'parent' };

      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);

        if (match) {
          baseProperties[match[1]] = value;
        }
      });

      const baseItem = { id: formData.get('id'), quantity, properties: baseProperties };

      if (formData.get('selling_plan')) {
        baseItem.selling_plan = formData.get('selling_plan');
      }

      // Shopify preserves property order, and that order is what the cart, checkout and order pages display. The
      // customer's own input leads; the underscore-prefixed keys above it are hidden, and "Part of" is context.
      const addOnItems = this.selectedItems.map((item) => {
        const properties = {
          _linked_id: linkedId,
          _linked_role: 'child',
          _linked_type: item.dataset.addonType
        };

        const textInput = item.querySelector('[data-personalization-text]');

        if (textInput) {
          properties[`${item.dataset.addonType} Text`] = textInput.value.trim();
        }

        properties['Part of'] = this.dataset.productTitle;

        // Add-ons scale with the base product: one charm per ring, one engraving per ring
        return { id: this.#variantIdFor(item), quantity, properties };
      });

      return [baseItem, ...addOnItems];
    }

    #reset() {
      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-personalization-toggle]');

        toggle.checked = false;

        const textInput = item.querySelector('[data-personalization-text]');

        if (textInput) {
          textInput.value = '';
          this.#updateCounter(item);
        }

        const select = item.querySelector('select');

        if (select) {
          select.selectedIndex = 0;
        }

        this.#syncItemState(item);
      });

      this.#updateTotal();
      this.#updatePaymentButton();
    }

    async #onSubmit(event) {
      const { form } = this;

      if (event.target !== form || this.selectedItems.length === 0) {
        return; // Not our form, or nothing personalized: let the theme handle this submission normally
      }

      event.preventDefault();
      event.stopPropagation();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      if (!this.#validate()) {
        return;
      }

      const submitButtons = Array.from(form.elements).filter((element) => element.type === 'submit');

      if (submitButtons.some((button) => button.getAttribute('aria-busy') === 'true')) {
        return;
      }

      submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));

      const sectionsToBundle = new Set();

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      let response;
      let responseJson;

      try {
        response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest' // Needed for Shopify to check inventory
          },
          body: JSON.stringify({
            items: this.#buildItems(generateLinkedId()),
            sections: [...sectionsToBundle].join(',')
          })
        });

        responseJson = await response.json();
      } catch (error) {
        document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: `${error}`, tone: 'error' } }));
        return;
      } finally {
        submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }

      if (!response.ok) {
        form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: responseJson['message'] } }));

        document.dispatchEvent(
          new CustomEvent('toast:show', { detail: { message: responseJson['description'] || responseJson['message'], tone: 'error' } })
        );

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      this.#reset();

      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent['sections'] = responseJson['sections'];

      const items = responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson];

      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart: cartContent } }));

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cartContent } })
      );

      if (window.themeVariables.settings.cartType === 'message') {
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: { message: window.themeVariables.strings.addedToCart.replace('{{product_title}}', items?.[0]?.title) }
          })
        );
      }
    }
  }

  window.customElements.define('personalization-form', PersonalizationForm);

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * CART RECONCILIATION
   * -------------------------------------------------------------------------------------------------------------------
   *
   * Two rules keep the cart consistent with what was added on the product page:
   *
   *   1. An add-on is never left in the cart without the product it belongs to. Removing a product removes its add-ons.
   *   2. An add-on always matches the quantity of the product it belongs to.
   *
   * Rule 1 is enforced up front by intercepting the remove control, so the orphaned state is never reached. The
   * "cart:change" listener at the bottom is the backstop for the paths we cannot intercept — chiefly a remove performed
   * without JavaScript, which is a plain link navigation.
   */
  let reconciling = false;

  /**
   * Applies a set of line item updates the way the theme's own <line-item> does, so the cart drawer, cart count and
   * bundled sections all refresh exactly as they would after a native quantity change.
   */
  const applyCartUpdates = async (updates, sourceElement) => {
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sectionsToBundle = new Set();

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    let response;

    try {
      response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, sections: [...sectionsToBundle].join(',') })
      });
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }

    if (!response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    if (window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
      return;
    }

    const cart = await response.json();

    sourceElement?.dispatchEvent(new CustomEvent('line-item:change', { bubbles: true, detail: { quantity: 0, cart } }));

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart } })
    );
  };

  /**
   * RULE 1 — remove a product and its add-ons in a single request, so the cart never renders an orphaned add-on.
   */
  document.documentElement.addEventListener(
    'click',
    (event) => {
      const link = event.target?.closest?.(`a[href*="${Shopify.routes.root}cart/change"]`);

      if (!link || new URL(link.href).searchParams.get('quantity') !== '0') {
        return;
      }

      const parentRow = link.closest('[data-personalization-role="parent"]');

      if (!parentRow) {
        return; // Not a personalized product: the theme removes it on its own
      }

      const linkedId = parentRow.dataset.personalizationLinkedId;
      const keys = new Set([parentRow.dataset.personalizationKey]);

      document
        .querySelectorAll(
          `[data-personalization-role="child"][data-personalization-linked-id="${CSS.escape(linkedId)}"]`
        )
        .forEach((child) => keys.add(child.dataset.personalizationKey));

      if (keys.size === 1) {
        return; // No add-ons attached anymore, nothing to cascade
      }

      event.preventDefault();
      event.stopPropagation();

      const updates = {};
      keys.forEach((key) => {
        updates[key] = 0;
      });

      reconciling = true;

      applyCartUpdates(updates, link.closest('line-item')).finally(() => {
        reconciling = false;
      });
    },
    { capture: true }
  );

  /**
   * RULE 1 (backstop) and RULE 2 — reconcile whatever the cart actually contains after any change.
   */
  const reconcileLinkedItems = async (cart) => {
    if (reconciling || !cart?.items) {
      return;
    }

    const parentQuantities = new Map();

    cart.items.forEach((item) => {
      if (item.properties?._linked_role === 'parent') {
        parentQuantities.set(item.properties._linked_id, item.quantity);
      }
    });

    const updates = {};

    cart.items.forEach((item) => {
      if (item.properties?._linked_role !== 'child') {
        return;
      }

      const parentQuantity = parentQuantities.get(item.properties._linked_id);

      if (parentQuantity === undefined) {
        updates[item.key] = 0; // Parent is gone: the add-on goes with it
      } else if (parentQuantity !== item.quantity) {
        updates[item.key] = parentQuantity;
      }
    });

    if (Object.keys(updates).length === 0) {
      return;
    }

    reconciling = true;

    try {
      await applyCartUpdates(updates);
    } finally {
      reconciling = false;
    }
  };

  document.documentElement.addEventListener('cart:change', (event) => {
    // An add already sends its add-ons at the right quantity, so only later changes need reconciling
    if (event.detail?.baseEvent !== 'line-item:change') {
      return;
    }

    reconcileLinkedItems(event.detail.cart);
  });

  /**
   * RULE 1 (backstop, on load) — "cart:change" never fires for a removal performed without JavaScript, because that is
   * a plain link navigation. Any add-on left behind by one is caught here, from the rendered markup rather than from a
   * cart request, so this costs nothing on the overwhelmingly common case where there is nothing to clean up.
   */
  const removeOrphansFromMarkup = () => {
    const parentIds = new Set(
      Array.from(document.querySelectorAll('[data-personalization-role="parent"]')).map(
        (element) => element.dataset.personalizationLinkedId
      )
    );

    const updates = {};

    document.querySelectorAll('[data-personalization-role="child"]').forEach((child) => {
      if (!parentIds.has(child.dataset.personalizationLinkedId)) {
        updates[child.dataset.personalizationKey] = 0;
      }
    });

    if (Object.keys(updates).length > 0) {
      applyCartUpdates(updates);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeOrphansFromMarkup, { once: true });
  } else {
    removeOrphansFromMarkup();
  }
})();
